// frontend/src/pages/Portal/OhtAdmin/Overview/components/OhtFsaCapacityMap.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { OhtFsaInsights } from "../../../../../api/ohtAdmin";
import "./OhtFsaCapacityMap.css";

type FsaTone = "good" | "warn" | "danger" | "neutral";

type FsaMapSourceRow = Partial<OhtFsaInsights> & {
  fsa?: string | null;
  fsaLabel?: string | null;
  isCoverageOnly?: boolean;
};

type FsaCoordinateSource = FsaMapSourceRow & {
  latitude?: number | null;
  longitude?: number | null;
  lat?: number | null;
  lng?: number | null;
  averageLatitude?: number | null;
  averageLongitude?: number | null;
  avgLatitude?: number | null;
  avgLongitude?: number | null;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  centreLatitude?: number | null;
  centreLongitude?: number | null;
  clinicLatitude?: number | null;
  clinicLongitude?: number | null;
};

type FsaPaceSource = FsaMapSourceRow & {
  pacePerDay?: number | null;
  fsaPacePerDay?: number | null;
  attachmentPacePerDay?: number | null;
  attachments30d?: number | null;
  Attachments30d?: number | null;
  attachmentsLast30Days?: number | null;
  AttachmentsLast30Days?: number | null;
};

type FsaCoordinate = {
  latitude: number;
  longitude: number;
};

type EnrichedFsaRow = FsaMapSourceRow & {
  fsa: string;
  fsaLabel: string;
  hasOperationalData: boolean;
  completedAgainstTarget: number;
  targetPatients: number;
  attachments: number;
  remainingCapacity: number;
  pipelineCount: number;
  clinicCount: number;
  doctorCount: number;
  acceptingDoctorCount: number;
  applications: number;
  avgDaysToAttachment: number | null;
  remainingTarget: number;
  capacityGap: number;
  pacePerDay: number | null;
  paceSourceLabel: string;
  estimatedDaysToTarget: number | null;
  etaLabel: string;
  tone: FsaTone;
  toneLabel: string;
  mapLatitude: number | null;
  mapLongitude: number | null;
  hasMapCoordinates: boolean;
};

type FsaBoundaryProperties = {
  fsa?: string;
  FSA?: string;
  CFSAUID?: string;
  cfsauid?: string;
  FSA_CODE?: string;
  fsaCode?: string;
  tone?: FsaTone;
  toneLabel?: string;
  fillColor?: string;
  pipelineCount?: number;
  capacityGap?: number;
  hasData?: boolean;
  [key: string]: unknown;
};

type FsaBoundaryFeature = GeoJSON.Feature<GeoJSON.Geometry, FsaBoundaryProperties>;
type FsaBoundaryFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Geometry,
  FsaBoundaryProperties
>;

type OhtFsaCapacityMapProps = {
  fsaRows: OhtFsaInsights[];

  /**
   * Full list of FSAs covered by the OHT. This is the geographic source of truth.
   * FSAs with no operational or insight row are still rendered as grey coverage-only areas.
   */
  ohtFsas?: string[];

  isLoading?: boolean;

  /**
   * Optional selected-range window used to calculate pace when the backend does
   * not return an FSA-specific pace. Example: 30 for Last 30 days, 90 for Last 90 days.
   */
  paceWindowDays?: number | null;

  /**
   * Optional label for the pace window, shown in tooltips/detail text.
   * Example: "Last 90 days".
   */
  paceWindowLabel?: string | null;
};

const MAPBOX_STYLE = "mapbox://styles/mapbox/light-v11";
const FSA_BOUNDARY_SOURCE_ID = "oht-fsa-boundaries-source";
const FSA_BOUNDARY_FILL_LAYER_ID = "oht-fsa-boundaries-fill";
const FSA_BOUNDARY_LINE_LAYER_ID = "oht-fsa-boundaries-line";
const FSA_BOUNDARY_SELECTED_LAYER_ID = "oht-fsa-boundaries-selected-line";
const FSA_BOUNDARY_GEOJSON_URL = `${
  process.env.PUBLIC_URL || ""
}/assets/geo/oht-fsa-boundaries.geojson`;

const DEFAULT_MAP_CENTER: FsaCoordinate = {
  latitude: 43.4675,
  longitude: -79.6877,
};

const FSA_FALLBACK_CENTERS: Record<string, FsaCoordinate> = {
  L0P: { latitude: 43.55, longitude: -79.88 },
  L6H: { latitude: 43.484, longitude: -79.705 },
  L6J: { latitude: 43.459, longitude: -79.672 },
  L6K: { latitude: 43.444, longitude: -79.683 },
  L6L: { latitude: 43.413, longitude: -79.724 },
  L6M: { latitude: 43.455, longitude: -79.75 },
  L6T: { latitude: 43.707, longitude: -79.698 },
  L6V: { latitude: 43.706, longitude: -79.756 },
  L6W: { latitude: 43.681, longitude: -79.738 },
  L6X: { latitude: 43.689, longitude: -79.793 },
  L6Y: { latitude: 43.653, longitude: -79.759 },
  L6Z: { latitude: 43.726, longitude: -79.815 },
  L7G: { latitude: 43.647, longitude: -79.925 },
  L7J: { latitude: 43.63, longitude: -80.042 },
  L7L: { latitude: 43.394, longitude: -79.762 },
  L7M: { latitude: 43.393, longitude: -79.828 },
  L7N: { latitude: 43.345, longitude: -79.793 },
  L7P: { latitude: 43.36, longitude: -79.841 },
  L7R: { latitude: 43.327, longitude: -79.798 },
  L7S: { latitude: 43.318, longitude: -79.808 },
  L7T: { latitude: 43.318, longitude: -79.86 },
  L9E: { latitude: 43.485, longitude: -79.82 },
  L9H: { latitude: 43.323, longitude: -79.93 },
  L9T: { latitude: 43.518, longitude: -79.877 },
  L9V: { latitude: 43.914, longitude: -80.105 },
};

function getMapboxAccessToken(): string {
  const env = process.env as Record<string, string | undefined>;

  return (
    env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
    env.REACT_APP_MAPBOX_TOKEN ||
    env.MAPBOX_ACCESS_TOKEN ||
    ""
  );
}

function toNumber(value: number | null | undefined): number {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 0;
  }

  return Number(value);
}

function toOptionalNumber(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }

  return Number(value);
}

function fmtNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return Number(value).toLocaleString();
}

function fmtSigned(value: number): string {
  if (value > 0) return `+${value.toLocaleString()}`;
  return value.toLocaleString();
}

function fmtDays(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${Number(value).toFixed(1)} days`;
}

function fmtPace(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  if (Number(value) === 0) return "0 / day";
  return `${Number(value).toFixed(2)} / day`;
}

function fmtEta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  if (Number(value) <= 0) return "Complete";
  if (Math.ceil(Number(value)) === 1) return "1 day";
  return `${Math.ceil(Number(value)).toLocaleString()} days`;
}

function getFsaTone(row: {
  targetPatients: number;
  remainingTarget: number;
  remainingCapacity: number;
  capacityGap: number;
}): { tone: FsaTone; label: string } {
  if (row.targetPatients <= 0) {
    return {
      tone: "neutral",
      label: "No active FSA target",
    };
  }

  if (row.remainingTarget <= 0) {
    return {
      tone: "good",
      label: "Target complete",
    };
  }

  if (row.capacityGap >= 0) {
    return {
      tone: "good",
      label: "Capacity covers target",
    };
  }

  const coverageRatio =
    row.remainingTarget > 0
      ? row.remainingCapacity / Math.max(row.remainingTarget, 1)
      : 1;

  if (coverageRatio >= 0.75) {
    return {
      tone: "warn",
      label: "Near target capacity",
    };
  }

  return {
    tone: "danger",
    label: "Capacity shortfall",
  };
}

function toneRank(tone: FsaTone): number {
  if (tone === "danger") return 0;
  if (tone === "warn") return 1;
  if (tone === "neutral") return 2;
  return 3;
}

function toneColor(tone: FsaTone): string {
  if (tone === "good") return "#16a34a";
  if (tone === "warn") return "#d97706";
  if (tone === "danger") return "#dc2626";
  return "#64748b";
}

function normalizeFsa(value: unknown): string {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .slice(0, 3);
}

function resolveBoundaryFsa(
  properties: FsaBoundaryProperties | null | undefined
): string {
  if (!properties) return "";

  return (
    normalizeFsa(properties.fsa) ||
    normalizeFsa(properties.FSA) ||
    normalizeFsa(properties.CFSAUID) ||
    normalizeFsa(properties.cfsauid) ||
    normalizeFsa(properties.FSA_CODE) ||
    normalizeFsa(properties.fsaCode)
  );
}

function resolveCoordinates(row: FsaMapSourceRow): FsaCoordinate | null {
  const source = row as FsaCoordinateSource;

  const latitude =
    toOptionalNumber(source.latitude) ??
    toOptionalNumber(source.lat) ??
    toOptionalNumber(source.averageLatitude) ??
    toOptionalNumber(source.avgLatitude) ??
    toOptionalNumber(source.centerLatitude) ??
    toOptionalNumber(source.centreLatitude) ??
    toOptionalNumber(source.clinicLatitude);

  const longitude =
    toOptionalNumber(source.longitude) ??
    toOptionalNumber(source.lng) ??
    toOptionalNumber(source.averageLongitude) ??
    toOptionalNumber(source.avgLongitude) ??
    toOptionalNumber(source.centerLongitude) ??
    toOptionalNumber(source.centreLongitude) ??
    toOptionalNumber(source.clinicLongitude);

  if (latitude !== null && longitude !== null) {
    return { latitude, longitude };
  }

  const fsa = normalizeFsa(row.fsa);
  return FSA_FALLBACK_CENTERS[fsa] || null;
}

function getAverageCenter(rows: EnrichedFsaRow[]): FsaCoordinate {
  const rowsWithCoordinates = rows.filter(
    (row) => row.mapLatitude !== null && row.mapLongitude !== null
  );

  if (rowsWithCoordinates.length === 0) {
    return DEFAULT_MAP_CENTER;
  }

  const totals = rowsWithCoordinates.reduce(
    (acc, row) => {
      acc.latitude += row.mapLatitude || 0;
      acc.longitude += row.mapLongitude || 0;
      return acc;
    },
    { latitude: 0, longitude: 0 }
  );

  return {
    latitude: totals.latitude / rowsWithCoordinates.length,
    longitude: totals.longitude / rowsWithCoordinates.length,
  };
}

function resolveFsaPace({
  row,
  selectedRangeAttachments,
  paceWindowDays,
  paceWindowLabel,
}: {
  row: FsaMapSourceRow;
  selectedRangeAttachments: number;
  paceWindowDays?: number | null;
  paceWindowLabel?: string | null;
}): { pacePerDay: number | null; sourceLabel: string } {
  const source = row as FsaPaceSource;

  const directPace =
    toOptionalNumber(source.pacePerDay) ??
    toOptionalNumber(source.fsaPacePerDay) ??
    toOptionalNumber(source.attachmentPacePerDay);

  if (directPace !== null) {
    return {
      pacePerDay: Math.max(directPace, 0),
      sourceLabel: "Backend pace",
    };
  }

  const attachments30d =
    toOptionalNumber(source.attachments30d) ??
    toOptionalNumber(source.Attachments30d) ??
    toOptionalNumber(source.attachmentsLast30Days) ??
    toOptionalNumber(source.AttachmentsLast30Days);

  if (attachments30d !== null) {
    return {
      pacePerDay: Math.max(attachments30d, 0) / 30,
      sourceLabel: "Last 30 days",
    };
  }

  if (paceWindowDays && paceWindowDays > 0) {
    return {
      pacePerDay: Math.max(selectedRangeAttachments, 0) / paceWindowDays,
      sourceLabel: paceWindowLabel || `Selected ${paceWindowDays}-day range`,
    };
  }

  return {
    pacePerDay: null,
    sourceLabel: "Pace unavailable",
  };
}

function buildEtaLabel(row: {
  targetPatients: number;
  remainingTarget: number;
  pacePerDay: number | null;
}): { estimatedDaysToTarget: number | null; etaLabel: string } {
  if (row.targetPatients <= 0) {
    return {
      estimatedDaysToTarget: null,
      etaLabel: "No target",
    };
  }

  if (row.remainingTarget <= 0) {
    return {
      estimatedDaysToTarget: 0,
      etaLabel: "Complete",
    };
  }

  if (row.pacePerDay === null) {
    return {
      estimatedDaysToTarget: null,
      etaLabel: "Pace unavailable",
    };
  }

  if (row.pacePerDay <= 0) {
    return {
      estimatedDaysToTarget: null,
      etaLabel: "No current pace",
    };
  }

  const estimatedDaysToTarget = Math.ceil(row.remainingTarget / row.pacePerDay);

  return {
    estimatedDaysToTarget,
    etaLabel: fmtEta(estimatedDaysToTarget),
  };
}

function buildCoverageOnlyRow(fsa: string): FsaMapSourceRow {
  return {
    fsa,
    fsaLabel: `FSA ${fsa}`,
    clinicCount: 0,
    doctorCount: 0,
    acceptingDoctorCount: 0,
    remainingCapacity: 0,
    applications: 0,
    attachments: 0,
    pipelineCount: 0,
    targetPatients: 0,
    gapVsTarget: 0,
    avgDaysToAttachment: null,
    isCoverageOnly: true,
  };
}

function buildMapSourceRows({
  insightRows,
  ohtFsas,
}: {
  insightRows: OhtFsaInsights[];
  ohtFsas: string[];
}): FsaMapSourceRow[] {
  const dataByFsa = new Map<string, OhtFsaInsights>();

  insightRows.forEach((row) => {
    const fsa = normalizeFsa(row.fsa);
    if (fsa) dataByFsa.set(fsa, row);
  });

  const allFsaKeys = new Set<string>();

  ohtFsas.forEach((fsa) => {
    const normalized = normalizeFsa(fsa);
    if (normalized) allFsaKeys.add(normalized);
  });

  dataByFsa.forEach((_row, fsa) => {
    if (fsa) allFsaKeys.add(fsa);
  });

  return Array.from(allFsaKeys)
    .sort((a, b) => a.localeCompare(b))
    .map((fsa) => dataByFsa.get(fsa) || buildCoverageOnlyRow(fsa));
}

function buildFsaInsightText(row: EnrichedFsaRow): string {
  if (!row.hasOperationalData) {
    return `This FSA is part of the OHT coverage area, but no operational or FSA insight data is currently available for this FSA. It remains visible so the OHT can understand the full geographic footprint. This may mean there are no scoped clinics, no active patient requests, no declared capacity, or no active target data for this area yet.`;
  }

  if (row.targetPatients <= 0) {
    if (row.remainingCapacity > 0) {
      return `No active FSA target is available for this area, but there are ${fmtNumber(
        row.remainingCapacity
      )} remaining declared space(s). The OHT can monitor this as available capacity, but the system cannot determine whether the FSA is ahead, behind, or appropriately resourced until a target is assigned.`;
    }

    return "No active FSA target is available for this area. Capacity and pipeline can still be monitored for planning, but the system cannot determine target progress until a target is assigned.";
  }

  if (row.remainingTarget <= 0) {
    if (row.remainingCapacity > 0) {
      return `This FSA appears to have reached its active target and still has ${fmtNumber(
        row.remainingCapacity
      )} remaining declared space(s). The OHT may reduce routine routing pressure here or consider this area for overflow if patient preferences, geography, and provider intake criteria match.`;
    }

    return "This FSA appears to have reached its active target. The OHT may reduce routine routing pressure here and continue monitoring future capacity declarations.";
  }

  if (row.capacityGap < 0) {
    return `This FSA has ${fmtNumber(
      row.remainingTarget
    )} patient(s) left to attach against the target, but only ${fmtNumber(
      row.remainingCapacity
    )} remaining declared space(s). Even if all current capacity is used, the FSA is short by ${fmtNumber(
      Math.abs(row.capacityGap)
    )} space(s). The OHT may need to confirm provider capacity, add capacity, or consider routing suitable patients to nearby surplus areas.`;
  }

  if (row.pacePerDay === null) {
    return `This FSA has enough remaining capacity to cover the target gap, but attachment pace cannot be assessed from the current data. The OHT can use this as a capacity signal, but should confirm whether recent attachment activity is actually occurring before relying on this area to close the target gap.`;
  }

  if (row.pacePerDay <= 0) {
    return `This FSA has enough remaining capacity to cover the target gap, but there is no current attachment pace in the selected period. If this FSA is expected to contribute to the target, the OHT may need to review whether requests are being routed, accepted, and completed.`;
  }

  if (row.estimatedDaysToTarget !== null && row.estimatedDaysToTarget > 120) {
    return `This FSA has enough remaining capacity to cover the target gap, but at the current pace it may take about ${fmtEta(
      row.estimatedDaysToTarget
    )} to close the remaining target. This suggests the main issue may be attachment flow or workflow speed rather than declared capacity.`;
  }

  if (row.tone === "warn") {
    return `This FSA is close to its capacity target. Current capacity may cover most of the remaining need, but there is limited buffer if the pipeline grows or capacity declarations change. At the current pace, the estimated time to close the target gap is ${fmtEta(
      row.estimatedDaysToTarget
    )}. Continued monitoring is recommended.`;
  }

  return `This FSA currently has enough remaining capacity to cover the remaining target. At the current pace, the estimated time to close the target gap is ${fmtEta(
    row.estimatedDaysToTarget
  )}. Continue routing suitable patients while monitoring capacity updates and provider intake criteria.`;
}

function prepareBoundaryData(
  boundaryData: FsaBoundaryFeatureCollection | null,
  rows: EnrichedFsaRow[]
): FsaBoundaryFeatureCollection | null {
  if (!boundaryData || !Array.isArray(boundaryData.features)) {
    return null;
  }

  const rowByFsa = new Map(
    rows
      .map((row) => [normalizeFsa(row.fsa), row] as const)
      .filter(([fsa]) => Boolean(fsa))
  );

  const features: FsaBoundaryFeature[] = boundaryData.features
    .map((feature) => {
      const fsa = resolveBoundaryFsa(feature.properties);
      const row = rowByFsa.get(fsa);

      if (!fsa || !row || !feature.geometry) {
        return null;
      }

      const preparedFeature: FsaBoundaryFeature = {
        ...feature,
        id: fsa,
        properties: {
          ...(feature.properties || {}),
          fsa,
          tone: row.tone,
          toneLabel: row.toneLabel,
          fillColor: toneColor(row.tone),
          pipelineCount: row.pipelineCount,
          capacityGap: row.capacityGap,
          hasData: row.hasOperationalData,
        },
      };

      return preparedFeature;
    })
    .filter((feature): feature is FsaBoundaryFeature => Boolean(feature));

  return {
    type: "FeatureCollection",
    features,
  };
}

function addOrUpdateBoundaryLayers(
  map: mapboxgl.Map,
  boundaryData: FsaBoundaryFeatureCollection,
  selectedFsa: string | null
): void {
  const existingSource = map.getSource(FSA_BOUNDARY_SOURCE_ID) as
    | mapboxgl.GeoJSONSource
    | undefined;

  if (existingSource) {
    existingSource.setData(boundaryData);
  } else {
    map.addSource(FSA_BOUNDARY_SOURCE_ID, {
      type: "geojson",
      data: boundaryData,
    });
  }

  if (!map.getLayer(FSA_BOUNDARY_FILL_LAYER_ID)) {
    map.addLayer({
      id: FSA_BOUNDARY_FILL_LAYER_ID,
      type: "fill",
      source: FSA_BOUNDARY_SOURCE_ID,
      paint: {
        "fill-color": [
          "match",
          ["get", "tone"],
          "good",
          "#16a34a",
          "warn",
          "#d97706",
          "danger",
          "#dc2626",
          "neutral",
          "#64748b",
          "#64748b",
        ],
        "fill-opacity": [
          "case",
          ["==", ["get", "hasData"], false],
          0.12,
          ["==", ["get", "tone"], "neutral"],
          0.18,
          0.34,
        ],
      },
    } as mapboxgl.AnyLayer);
  }

  if (!map.getLayer(FSA_BOUNDARY_LINE_LAYER_ID)) {
    map.addLayer({
      id: FSA_BOUNDARY_LINE_LAYER_ID,
      type: "line",
      source: FSA_BOUNDARY_SOURCE_ID,
      paint: {
        "line-color": "#0f172a",
        "line-opacity": [
          "case",
          ["==", ["get", "hasData"], false],
          0.2,
          0.32,
        ],
        "line-width": 1.2,
      },
    } as mapboxgl.AnyLayer);
  }

  if (!map.getLayer(FSA_BOUNDARY_SELECTED_LAYER_ID)) {
    map.addLayer({
      id: FSA_BOUNDARY_SELECTED_LAYER_ID,
      type: "line",
      source: FSA_BOUNDARY_SOURCE_ID,
      filter: ["==", ["get", "fsa"], selectedFsa || ""],
      paint: {
        "line-color": "#0f172a",
        "line-opacity": 0.96,
        "line-width": 3.4,
      },
    } as mapboxgl.AnyLayer);
  } else {
    map.setFilter(FSA_BOUNDARY_SELECTED_LAYER_ID, [
      "==",
      ["get", "fsa"],
      selectedFsa || "",
    ] as mapboxgl.FilterSpecification);
  }
}

export function OhtFsaCapacityMap({
  fsaRows,
  ohtFsas = [],
  isLoading = false,
  paceWindowDays = null,
  paceWindowLabel = null,
}: OhtFsaCapacityMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const boundaryHandlersAttachedRef = useRef(false);
  const lastStableRowsRef = useRef<OhtFsaInsights[]>([]);
  const lastStableOhtFsasRef = useRef<string[]>([]);
  const [selectedFsa, setSelectedFsa] = useState<string | null>(null);
  const [rawBoundaryData, setRawBoundaryData] =
    useState<FsaBoundaryFeatureCollection | null>(null);
  const [boundaryLoadFailed, setBoundaryLoadFailed] = useState(false);

  const mapboxAccessToken = getMapboxAccessToken();

  const incomingFsaRows = useMemo(
    () => (Array.isArray(fsaRows) ? fsaRows : []),
    [fsaRows]
  );

  const incomingOhtFsas = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(ohtFsas) ? ohtFsas : [])
            .map((fsa) => normalizeFsa(fsa))
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [ohtFsas]
  );

  useEffect(() => {
    if (incomingFsaRows.length > 0) {
      lastStableRowsRef.current = incomingFsaRows;
    }
  }, [incomingFsaRows]);

  useEffect(() => {
    if (incomingOhtFsas.length > 0) {
      lastStableOhtFsasRef.current = incomingOhtFsas;
    }
  }, [incomingOhtFsas]);

  useEffect(() => {
    let isMounted = true;

    fetch(FSA_BOUNDARY_GEOJSON_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error("FSA boundary file could not be loaded.");
        }

        return response.json() as Promise<FsaBoundaryFeatureCollection>;
      })
      .then((data) => {
        if (!isMounted) return;
        setRawBoundaryData(data);
        setBoundaryLoadFailed(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setRawBoundaryData(null);
        setBoundaryLoadFailed(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const enrichedRows = useMemo<EnrichedFsaRow[]>(() => {
    const insightRows =
      incomingFsaRows.length > 0
        ? incomingFsaRows
        : isLoading
          ? lastStableRowsRef.current
          : [];

    const coverageFsas =
      incomingOhtFsas.length > 0
        ? incomingOhtFsas
        : isLoading
          ? lastStableOhtFsasRef.current
          : [];

    const rows = buildMapSourceRows({
      insightRows,
      ohtFsas: coverageFsas,
    });

    return rows
      .map((row) => {
        const normalizedFsa = normalizeFsa(row.fsa);
        const hasOperationalData = !row.isCoverageOnly;

        const targetPatients = toNumber(row.targetPatients);
        const selectedRangeAttachments = toNumber(row.attachments);
        const targetGap = toOptionalNumber(row.gapVsTarget);
        const remainingCapacity = toNumber(row.remainingCapacity);
        const pipelineCount = toNumber(row.pipelineCount);
        const clinicCount = toNumber(row.clinicCount);
        const doctorCount = toNumber(row.doctorCount);
        const acceptingDoctorCount = toNumber(row.acceptingDoctorCount);
        const applications = toNumber(row.applications);
        const avgDaysToAttachment = toOptionalNumber(row.avgDaysToAttachment);
        const coordinates = resolveCoordinates(row);

        const remainingTarget =
          targetPatients > 0
            ? Math.max(targetGap ?? targetPatients - selectedRangeAttachments, 0)
            : 0;

        const completedAgainstTarget =
          targetPatients > 0
            ? Math.max(targetPatients - remainingTarget, 0)
            : selectedRangeAttachments;

        const capacityGap = remainingCapacity - remainingTarget;

        const pace = hasOperationalData
          ? resolveFsaPace({
              row,
              selectedRangeAttachments,
              paceWindowDays,
              paceWindowLabel,
            })
          : {
              pacePerDay: null,
              sourceLabel: "No operational data",
            };

        const eta = buildEtaLabel({
          targetPatients,
          remainingTarget,
          pacePerDay: pace.pacePerDay,
        });

        const toneResult = hasOperationalData
          ? getFsaTone({
              targetPatients,
              remainingTarget,
              remainingCapacity,
              capacityGap,
            })
          : {
              tone: "neutral" as FsaTone,
              label: "OHT coverage / no data",
            };

        return {
          ...row,
          fsa: normalizedFsa,
          fsaLabel:
            row.fsaLabel || (normalizedFsa ? `FSA ${normalizedFsa}` : "Unknown FSA"),
          hasOperationalData,
          targetPatients,
          attachments: selectedRangeAttachments,
          completedAgainstTarget,
          remainingCapacity,
          pipelineCount,
          clinicCount,
          doctorCount,
          acceptingDoctorCount,
          applications,
          avgDaysToAttachment,
          remainingTarget,
          capacityGap,
          pacePerDay: pace.pacePerDay,
          paceSourceLabel: pace.sourceLabel,
          estimatedDaysToTarget: eta.estimatedDaysToTarget,
          etaLabel: eta.etaLabel,
          tone: toneResult.tone,
          toneLabel: toneResult.label,
          mapLatitude: coordinates?.latitude ?? null,
          mapLongitude: coordinates?.longitude ?? null,
          hasMapCoordinates: Boolean(coordinates),
        };
      })
      .sort((a, b) => {
        const toneDiff = toneRank(a.tone) - toneRank(b.tone);
        if (toneDiff !== 0) return toneDiff;

        if (a.hasOperationalData !== b.hasOperationalData) {
          return a.hasOperationalData ? -1 : 1;
        }

        const pipelineDiff = toNumber(b.pipelineCount) - toNumber(a.pipelineCount);
        if (pipelineDiff !== 0) return pipelineDiff;

        return String(a.fsa || "").localeCompare(String(b.fsa || ""));
      });
  }, [
    incomingFsaRows,
    incomingOhtFsas,
    isLoading,
    paceWindowDays,
    paceWindowLabel,
  ]);

  const preparedBoundaryData = useMemo(
    () => prepareBoundaryData(rawBoundaryData, enrichedRows),
    [rawBoundaryData, enrichedRows]
  );

  const boundaryFeatureCount = preparedBoundaryData?.features.length ?? 0;

  const rowsWithMapCoordinates = useMemo(
    () => enrichedRows.filter((row) => row.hasMapCoordinates),
    [enrichedRows]
  );

  const selectedRow =
    enrichedRows.find((row) => row.fsa === selectedFsa) || enrichedRows[0] || null;

  const selectedMapRow = selectedRow?.hasMapCoordinates ? selectedRow : null;
  const canRenderMap = Boolean(mapboxAccessToken && enrichedRows.length > 0);

  useEffect(() => {
    if (!canRenderMap || !mapContainerRef.current || mapRef.current) {
      return;
    }

    const center = getAverageCenter(rowsWithMapCoordinates);
    mapboxgl.accessToken = mapboxAccessToken;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center: [center.longitude, center.latitude],
      zoom: rowsWithMapCoordinates.length > 1 ? 9.5 : 11,
      attributionControl: false,
      cooperativeGestures: true,
    });

    mapRef.current.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      "bottom-right"
    );
    mapRef.current.addControl(new mapboxgl.AttributionControl({ compact: true }));
  }, [canRenderMap, mapboxAccessToken, rowsWithMapCoordinates]);

  useEffect(() => {
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !canRenderMap || !preparedBoundaryData || boundaryFeatureCount === 0) {
      return;
    }

    const applyBoundaryLayers = () => {
      addOrUpdateBoundaryLayers(map, preparedBoundaryData, selectedRow?.fsa || null);

      if (!boundaryHandlersAttachedRef.current) {
        map.on("click", FSA_BOUNDARY_FILL_LAYER_ID, (event) => {
          const feature = event.features?.[0] as FsaBoundaryFeature | undefined;
          const fsa = resolveBoundaryFsa(feature?.properties);
          if (fsa) setSelectedFsa(fsa);
        });

        map.on("mouseenter", FSA_BOUNDARY_FILL_LAYER_ID, () => {
          map.getCanvas().style.cursor = "pointer";
        });

        map.on("mouseleave", FSA_BOUNDARY_FILL_LAYER_ID, () => {
          map.getCanvas().style.cursor = "";
        });

        boundaryHandlersAttachedRef.current = true;
      }
    };

    if (map.loaded()) {
      applyBoundaryLayers();
    } else {
      map.once("load", applyBoundaryLayers);
    }
  }, [boundaryFeatureCount, canRenderMap, preparedBoundaryData, selectedRow?.fsa]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !canRenderMap || !map.getLayer(FSA_BOUNDARY_SELECTED_LAYER_ID)) {
      return;
    }

    map.setFilter(FSA_BOUNDARY_SELECTED_LAYER_ID, [
      "==",
      ["get", "fsa"],
      selectedRow?.fsa || "",
    ] as mapboxgl.FilterSpecification);
  }, [canRenderMap, selectedRow?.fsa]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !canRenderMap || rowsWithMapCoordinates.length === 0) return;

    if (
      selectedMapRow &&
      selectedMapRow.mapLatitude !== null &&
      selectedMapRow.mapLongitude !== null
    ) {
      map.easeTo({
        center: [selectedMapRow.mapLongitude, selectedMapRow.mapLatitude],
        zoom: Math.max(map.getZoom(), rowsWithMapCoordinates.length > 1 ? 10 : 11),
        duration: 500,
      });
      return;
    }

    if (rowsWithMapCoordinates.length === 1) {
      const row = rowsWithMapCoordinates[0];
      if (row.mapLatitude !== null && row.mapLongitude !== null) {
        map.easeTo({
          center: [row.mapLongitude, row.mapLatitude],
          zoom: 11,
          duration: 400,
        });
      }
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    rowsWithMapCoordinates.forEach((row) => {
      if (row.mapLatitude !== null && row.mapLongitude !== null) {
        bounds.extend([row.mapLongitude, row.mapLatitude]);
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: 72,
        maxZoom: 11,
        duration: 500,
      });
    }
  }, [canRenderMap, rowsWithMapCoordinates, selectedMapRow]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !canRenderMap) return;

    const resizeTimer = window.setTimeout(() => {
      map.resize();
    }, 0);

    return () => {
      window.clearTimeout(resizeTimer);
    };
  }, [boundaryFeatureCount, canRenderMap, enrichedRows.length, isLoading]);

  if (enrichedRows.length === 0 && isLoading) {
    return (
      <section className="oht-fsa-map-card">
        <div className="oht-fsa-map-header">
          <div>
            <h3>FSA capacity pressure map</h3>
            <p>
              Loading OHT coverage FSAs, FSA-level capacity, targets, and active
              patient pipeline.
            </p>
          </div>
        </div>

        <div className="oht-fsa-map-loading">
          <div className="oht-fsa-map-loading-grid">
            {Array.from({ length: 9 }).map((_, index) => (
              <div
                className="oht-fsa-map-loading-tile"
                key={`fsa-map-loading-${index}`}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (enrichedRows.length === 0) {
    return (
      <section className="oht-fsa-map-card">
        <div className="oht-fsa-map-header">
          <div>
            <h3>FSA capacity pressure map</h3>
            <p>
              Once OHT coverage or FSA insight data is available, this section
              will show geographic pressure across the OHT.
            </p>
          </div>
        </div>

        <div className="oht-fsa-map-empty">
          No OHT FSA coverage or FSA-level insight is available yet.
        </div>
      </section>
    );
  }

  return (
    <section className="oht-fsa-map-card">
      <div className="oht-fsa-map-header">
        <div>
          <h3>FSA capacity pressure map</h3>
          <p>
            FSA boundaries show the full OHT coverage area. FSAs with no current
            operational or insight data remain visible in grey.
          </p>
        </div>

        <div className="oht-fsa-map-legend" aria-label="Map legend">
          <span>
            <i className="oht-fsa-map-dot oht-fsa-map-dot--good" />
            Capacity covers target
          </span>
          <span>
            <i className="oht-fsa-map-dot oht-fsa-map-dot--warn" />
            Near target
          </span>
          <span>
            <i className="oht-fsa-map-dot oht-fsa-map-dot--danger" />
            Shortfall
          </span>
          <span>
            <i className="oht-fsa-map-dot oht-fsa-map-dot--neutral" />
            No data / no target
          </span>
        </div>
      </div>

      <div className="oht-fsa-map-layout">
        <div className="oht-fsa-map-canvas oht-fsa-map-canvas--real-map">
          {canRenderMap ? (
            <div
              ref={mapContainerRef}
              className="oht-fsa-mapbox"
              aria-label="Map showing full OHT FSA coverage and capacity pressure"
            />
          ) : (
            <div className="oht-fsa-map-unavailable">
              <h4>Map view unavailable</h4>
              {!mapboxAccessToken ? (
                <p>
                  Add REACT_APP_MAPBOX_ACCESS_TOKEN to the frontend environment
                  settings to enable the interactive OHT map.
                </p>
              ) : (
                <p>No OHT FSA coverage rows are available yet.</p>
              )}
            </div>
          )}

          {canRenderMap && boundaryLoadFailed ? (
            <div className="oht-fsa-map-boundary-warning">
              FSA boundary file not found. FSA list and detail cards are still
              available.
            </div>
          ) : null}

          {canRenderMap && isLoading ? (
            <div className="oht-fsa-map-refreshing" aria-live="polite">
              Updating map data…
            </div>
          ) : null}

          <div className="oht-fsa-map-overlay-list" aria-label="FSA map list">
            {enrichedRows.map((row) => {
              const isSelected = selectedRow?.fsa === row.fsa;

              return (
                <button
                  type="button"
                  key={row.fsa || row.fsaLabel}
                  className={`oht-fsa-map-chip oht-fsa-map-chip--${row.tone}${
                    isSelected ? " oht-fsa-map-chip--selected" : ""
                  }`}
                  onClick={() => setSelectedFsa(row.fsa || null)}
                  aria-pressed={isSelected}
                  title={
                    row.hasOperationalData
                      ? `${row.fsa}: ${row.toneLabel}`
                      : `${row.fsa}: OHT coverage area with no current data`
                  }
                >
                  <span>{row.fsa || "—"}</span>
                  <strong>{fmtNumber(row.pipelineCount)}</strong>
                </button>
              );
            })}
          </div>
        </div>

        {selectedRow ? (
          <aside className="oht-fsa-map-detail">
            <div className="oht-fsa-map-detail-header">
              <div>
                <span className="oht-fsa-map-eyebrow">Selected FSA</span>
                <h4>{selectedRow.fsaLabel || selectedRow.fsa || "—"}</h4>
              </div>

              <span
                className={`oht-fsa-map-status oht-fsa-map-status--${selectedRow.tone}`}
              >
                {selectedRow.toneLabel}
              </span>
            </div>

            <div className="oht-fsa-map-detail-grid">
              <div>
                <span>Target</span>
                <strong>{fmtNumber(selectedRow.targetPatients)}</strong>
              </div>
              <div>
                <span>Completed vs target</span>
                <strong>{fmtNumber(selectedRow.completedAgainstTarget)}</strong>
              </div>
              <div>
                <span>Remaining target</span>
                <strong>{fmtNumber(selectedRow.remainingTarget)}</strong>
              </div>
              <div>
                <span>Remaining capacity</span>
                <strong>{fmtNumber(selectedRow.remainingCapacity)}</strong>
              </div>
              <div>
                <span>Capacity position</span>
                <strong>{fmtSigned(selectedRow.capacityGap)}</strong>
              </div>
              <div>
                <span>Pipeline</span>
                <strong>{fmtNumber(selectedRow.pipelineCount)}</strong>
              </div>
              <div>
                <span>Pace</span>
                <strong>{fmtPace(selectedRow.pacePerDay)}</strong>
              </div>
              <div>
                <span>ETA to target</span>
                <strong>{selectedRow.etaLabel}</strong>
              </div>
            </div>

            <div className="oht-fsa-map-detail-section">
              <h5>Operational context</h5>
              <dl>
                <div>
                  <dt>Clinics</dt>
                  <dd>{fmtNumber(selectedRow.clinicCount)}</dd>
                </div>
                <div>
                  <dt>Doctors</dt>
                  <dd>{fmtNumber(selectedRow.doctorCount)}</dd>
                </div>
                <div>
                  <dt>Accepting doctors</dt>
                  <dd>{fmtNumber(selectedRow.acceptingDoctorCount)}</dd>
                </div>
                <div>
                  <dt>Applications</dt>
                  <dd>{fmtNumber(selectedRow.applications)}</dd>
                </div>
                <div>
                  <dt>Attachments in selected range</dt>
                  <dd>{fmtNumber(selectedRow.attachments)}</dd>
                </div>
                <div>
                  <dt>Pace source</dt>
                  <dd>{selectedRow.paceSourceLabel}</dd>
                </div>
                <div>
                  <dt>Average time to attachment</dt>
                  <dd>{fmtDays(selectedRow.avgDaysToAttachment)}</dd>
                </div>
              </dl>
            </div>

            <div className={`oht-fsa-map-insight oht-fsa-map-insight--${selectedRow.tone}`}>
              <p>{buildFsaInsightText(selectedRow)}</p>
            </div>
          </aside>
        ) : null}
      </div>
    </section>
  );
}

export default OhtFsaCapacityMap;